import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { listCards } from "../api/cards";
import { listInkLots, mixInkLots } from "../api/rawMaterials";
import { getMaterialVariantDimensions } from "../api/materials";
import { listDimensionOptions } from "../api/variantDimensions";

function lotKeyOf(lot) {
  return `${Number(lot.material_id)}:${lot.material_variant_id ? Number(lot.material_variant_id) : 0}::${String(lot.lot_no)}`;
}

export default function InkMixModal({ onClose, onMixed }) {
  const [lots, setLots] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [rows, setRows] = useState([{ lot_key: "", qty: "" }]);

  const [baseMaterialId, setBaseMaterialId] = useState("");
  const [templateDimensions, setTemplateDimensions] = useState([]);
  const [colorDimension, setColorDimension] = useState(null);
  const [colorOptions, setColorOptions] = useState([]);
  const [colorOptionId, setColorOptionId] = useState("");
  const [colorLoadError, setColorLoadError] = useState("");

  const [outPantoneCode, setOutPantoneCode] = useState("");
  const [outExpiryDate, setOutExpiryDate] = useState("");
  const [mixedByEmployeeCardId, setMixedByEmployeeCardId] = useState("");

  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);

  const lotByKey = useMemo(() => {
    const m = new Map();
    for (const l of lots || []) m.set(lotKeyOf(l), l);
    return m;
  }, [lots]);

  const selectedLots = useMemo(() => {
    const arr = [];
    for (const r of rows || []) {
      const k = String(r?.lot_key || "").trim();
      if (!k) continue;
      const lot = lotByKey.get(k);
      if (lot) arr.push(lot);
    }
    return arr;
  }, [rows, lotByKey]);

  useEffect(() => {
    if (!selectedLots.length) {
      setBaseMaterialId("");
      return;
    }
    const first = selectedLots[0];
    const id = first?.material_id ? String(first.material_id) : "";
    setBaseMaterialId(id);
  }, [selectedLots]);

  useEffect(() => {
    let cancelled = false;
    async function loadTemplateAndColors(materialIdStr) {
      const matId = Number(materialIdStr);
      if (!Number.isFinite(matId) || matId <= 0) {
        setTemplateDimensions([]);
        setColorDimension(null);
        setColorOptions([]);
        setColorOptionId("");
        setColorLoadError("");
        return;
      }

      setColorLoadError("");
      try {
        const dims = await getMaterialVariantDimensions(matId);
        if (cancelled) return;
        setTemplateDimensions(Array.isArray(dims) ? dims : []);

        const colorDim =
          (dims || []).find((d) => String(d?.key || "").toLowerCase() === "renk") ||
          (dims || []).find((d) => String(d?.key || "").toLowerCase() === "color") ||
          (dims || []).find((d) => String(d?.key || "").toLowerCase() === "colour") ||
          null;
        setColorDimension(colorDim);

        const dimId =
          colorDim?.dimension_id !== null && colorDim?.dimension_id !== undefined
            ? Number(colorDim.dimension_id)
            : colorDim?.id !== null && colorDim?.id !== undefined
              ? Number(colorDim.id)
              : null;
        if (!Number.isFinite(dimId) || !dimId) {
          setColorOptions([]);
          setColorOptionId("");
          setColorLoadError("Bu boya kartında 'Renk' varyantı bulunamadı.");
          return;
        }

        const opts = await listDimensionOptions(Number(dimId));
        if (cancelled) return;
        const items = Array.isArray(opts) ? opts : [];
        setColorOptions(items);

        if (colorOptionId && !items.some((o) => String(o?.id) === String(colorOptionId))) {
          setColorOptionId("");
        }
      } catch (err) {
        if (cancelled) return;
        console.error("INK MIX COLOR OPTIONS LOAD ERROR", err);
        setTemplateDimensions([]);
        setColorDimension(null);
        setColorOptions([]);
        setColorOptionId("");
        setColorLoadError(err?.response?.data?.error?.message || "Renk seçenekleri yüklenemedi");
      }
    }

    loadTemplateAndColors(baseMaterialId);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseMaterialId]);

  useEffect(() => {
    setLoading(true);
    setLoadError("");
    Promise.all([listInkLots(), listCards({ type: "employee" })])
      .then(([lotRows, empRows]) => {
        setLots(Array.isArray(lotRows) ? lotRows : []);
        setEmployees(Array.isArray(empRows) ? empRows : []);
      })
      .catch((err) => {
        console.error("INK MIX LOAD ERROR", err);
        setLots([]);
        setEmployees([]);
        setLoadError(err?.response?.data?.error?.message || "Lot/çalışan listeleri yüklenemedi");
      })
      .finally(() => setLoading(false));
  }, []);

  function addRow() {
    setRows((prev) => [...prev, { lot_key: "", qty: "" }]);
  }

  function removeRow(idx) {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  }

  function updateRow(idx, patch) {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  const totalQty = useMemo(() => {
    const sum = (rows || []).reduce((acc, r) => acc + (Number(r.qty) || 0), 0);
    return Number.isFinite(sum) ? sum : 0;
  }, [rows]);

  async function submit() {
    setSubmitError("");
    setResult(null);

    if (!rows.length) {
      setSubmitError("En az 1 satır ekleyin.");
      return;
    }

    const selectedKeys = rows.map((r) => String(r.lot_key || "").trim()).filter(Boolean);
    if (selectedKeys.length !== rows.length) {
      setSubmitError("Tüm satırlarda parti seçin.");
      return;
    }
    const unique = new Set(selectedKeys);
    if (unique.size !== selectedKeys.length) {
      setSubmitError("Aynı parti birden fazla kez seçilemez.");
      return;
    }

    const normalizedItems = rows.map((r) => {
      const key = String(r.lot_key || "").trim();
      const lot = lotByKey.get(key);
      const qty = Number(r.qty);
      return { key, lot, qty };
    });

    for (const it of normalizedItems) {
      if (!it.lot) {
        setSubmitError("Seçilen partilerden biri bulunamadı. Lütfen listeyi yenileyin.");
        return;
      }
      if (!it.lot.material_id) {
        setSubmitError("Seçilen partilerden birinde material_id eksik.");
        return;
      }
      if (!Number.isFinite(it.qty) || it.qty <= 0) {
        setSubmitError("Tüm satırlarda miktar pozitif olmalı.");
        return;
      }
      const onHand = Number(it.lot.on_hand_qty || 0);
      if (!Number.isFinite(onHand) || it.qty > onHand + 0.00005) {
        setSubmitError(`Kalan miktardan fazla tüketemezsiniz: ${it.lot.lot_no}`);
        return;
      }
    }

    // Prevent mixing offset/flexo by enforcing same material card.
    const matIds = normalizedItems.map((it) => Number(it.lot.material_id)).filter((x) => Number.isFinite(x));
    const uniqueMatIds = new Set(matIds);
    if (uniqueMatIds.size !== 1) {
      setSubmitError("Sadece aynı boya stok kartına ait partiler karıştırılabilir (offset/flekso karışamaz).");
      return;
    }

    if (!colorOptionId) {
      setSubmitError("Çıktı renk seçin.");
      return;
    }
    if (!outPantoneCode.trim()) {
      setSubmitError("Çıktı pantone zorunlu.");
      return;
    }
    if (!outExpiryDate) {
      setSubmitError("Çıktı son kullanma tarihi zorunlu.");
      return;
    }
    if (!mixedByEmployeeCardId) {
      setSubmitError("Karıştıran seçin.");
      return;
    }
    if (!Number.isFinite(totalQty) || totalQty <= 0) {
      setSubmitError("Toplam miktar pozitif olmalı.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        items: normalizedItems.map((it) => ({
          material_id: Number(it.lot.material_id),
          material_variant_id:
            it.lot.material_variant_id === null || it.lot.material_variant_id === undefined
              ? null
              : Number(it.lot.material_variant_id),
          lot_no: String(it.lot.lot_no),
          qty: Number(it.qty),
        })),
        output: {
          color_option_id: Number(colorOptionId),
          pantone_code: outPantoneCode.trim(),
          expiry_date: outExpiryDate,
          mixed_by_employee_card_id: Number(mixedByEmployeeCardId),
        },
      };

      const d = await mixInkLots(payload);
      setResult(d);
      await onMixed?.(d);
    } catch (err) {
      console.error("INK MIX SUBMIT ERROR", err);
      setSubmitError(err?.response?.data?.error?.message || "Karıştırma başarısız");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          Boya Karıştır
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Mevcut partilerden tüketip yeni bir parti oluşturun (tek işlem).
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {loadError ? <Alert severity="error">{loadError}</Alert> : null}
          {submitError ? <Alert severity="error">{submitError}</Alert> : null}
          {result?.output?.lot_no ? (
            <Alert severity="success">
              Oluşturuldu. Lot: <b>{result.output.lot_no}</b> — Miktar: <b>{result.output.qty}</b> {result.output.uom || "kg"}
            </Alert>
          ) : null}

          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Tüketilecek Partiler
              </Typography>
              <Button onClick={addRow} disabled={saving} variant="outlined">
                + Satır Ekle
              </Button>
            </Stack>

            <Stack spacing={1}>
              {rows.map((r, idx) => {
                const lot = r.lot_key ? lotByKey.get(String(r.lot_key)) : null;
                const uom = lot?.material_unit || "kg";
                return (
                  <Grid container spacing={1} key={idx} alignItems="center">
                    <Grid item xs={12} md={8}>
                      <FormControl fullWidth size="small">
                        <InputLabel id={`lot-${idx}`}>Parti</InputLabel>
                        <Select
                          labelId={`lot-${idx}`}
                          label="Parti"
                          value={r.lot_key}
                          onChange={(e) => updateRow(idx, { lot_key: e.target.value })}
                          disabled={saving || loading}
                        >
                          <MenuItem value="">
                            <em>Seçiniz</em>
                          </MenuItem>
                          {lots.map((l) => {
                            const key = lotKeyOf(l);
                            const vName = l.material_variant_display_name ? ` — ${l.material_variant_display_name}` : "";
                            const label = `${l.lot_no}${vName} — ${l.color_name || "—"} — ${l.pantone_code || "—"} — kalan ${l.on_hand_qty} ${l.material_unit || "kg"}`;
                            return (
                              <MenuItem key={key} value={key}>
                                {label}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={8} md={3}>
                      <TextField
                        size="small"
                        label={`Miktar (${uom})`}
                        value={r.qty}
                        onChange={(e) => updateRow(idx, { qty: e.target.value })}
                        disabled={saving}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={4} md={1}>
                      <Button
                        color="inherit"
                        onClick={() => removeRow(idx)}
                        disabled={saving || rows.length <= 1}
                        fullWidth
                      >
                        Sil
                      </Button>
                    </Grid>
                  </Grid>
                );
              })}
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Toplam çıktı: <b>{totalQty || 0}</b> kg
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
              Çıktı Bilgileri
            </Typography>
            <Grid container spacing={2}>
              {colorLoadError ? (
                <Grid item xs={12}>
                  <Alert severity="warning">{colorLoadError}</Alert>
                </Grid>
              ) : null}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small" required>
                  <InputLabel id="out-color-label">Renk</InputLabel>
                  <Select
                    labelId="out-color-label"
                    label="Renk"
                    value={colorOptionId}
                    onChange={(e) => setColorOptionId(e.target.value)}
                    disabled={saving || !baseMaterialId || !!colorLoadError}
                  >
                    <MenuItem value="">
                      <em>Seçiniz</em>
                    </MenuItem>
                    {colorOptions.map((o) => (
                      <MenuItem key={o.id} value={String(o.id)}>
                        {o.label || o.code || String(o.id)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  size="small"
                  required
                  fullWidth
                  label="Pantone"
                  value={outPantoneCode}
                  onChange={(e) => setOutPantoneCode(e.target.value)}
                  disabled={saving}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  size="small"
                  type="date"
                  required
                  fullWidth
                  label="Son Kullanma Tarihi"
                  value={outExpiryDate}
                  onChange={(e) => setOutExpiryDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={saving}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small" required>
                  <InputLabel id="mixed-by-label">Karıştıran</InputLabel>
                  <Select
                    labelId="mixed-by-label"
                    label="Karıştıran"
                    value={mixedByEmployeeCardId}
                    onChange={(e) => setMixedByEmployeeCardId(e.target.value)}
                    disabled={saving}
                  >
                    <MenuItem value="">
                      <em>Seçiniz</em>
                    </MenuItem>
                    {employees.map((c) => (
                      <MenuItem key={c.id} value={String(c.id)}>
                        {(c.code ? `${c.code} - ` : "") + c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Kapat
        </Button>
        <Button onClick={submit} variant="contained" disabled={saving || loading}>
          {saving ? "Karıştırılıyor..." : "Onayla"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

